import { api } from "@/lib/apiClient";
import { toast } from "@/lib/toast";

export interface PrintJobResult {
  ok?: boolean | null;
  outcome?: string;
  message?: string;
  printer?: string;
  print_id?: string | null;
  cups_job_id?: string | null;
  cups_job_state?: string | null;
  cups_job_status_text?: string | null;
  cups_job_reasons?: string[];
  cups_printer_state?: string | null;
  cups_printer_enabled?: boolean | null;
  cups_printer_reasons?: string[];
  issues?: string[];
  lp_stdout?: string;
  lp_stderr?: string;
  pack_jobs?: number;
  pack_documents?: number;
}

export interface PrintJobStatus {
  job_id: string;
  status: "queued" | "running" | "success" | "failed";
  printer?: string;
  printer_name?: string;
  copies?: number;
  error?: string | null;
  result?: PrintJobResult | null;
  kind?: string;
}

export interface PrintQueueResponse {
  ok?: boolean;
  job_id?: string;
  printer_name?: string;
  copies?: number;
  status?: string;
  pack_jobs?: number;
  pack_documents?: number;
}

export interface PrintJobFeedback {
  variant: "success" | "warning" | "error";
  title: string;
  message: string;
}

export class PrintJobFeedbackError extends Error {
  feedback: PrintJobFeedback;
  job: PrintJobStatus;

  constructor(job: PrintJobStatus) {
    const feedback = formatPrintJobFeedback(job);
    super(feedback.message || feedback.title);
    this.name = "PrintJobFeedbackError";
    this.feedback = feedback;
    this.job = job;
  }
}

function sleep(ms: number): Promise<void> {
  return new Promise((resolve) => window.setTimeout(resolve, ms));
}

export async function getPrintJob(jobId: string): Promise<PrintJobStatus> {
  return api.get<PrintJobStatus>(`/print/jobs/${encodeURIComponent(jobId)}`, { cache: "no-store" });
}

export async function waitForPrintJob(jobId: string, maxMs = 95000): Promise<PrintJobStatus> {
  const started = Date.now();
  while (Date.now() - started < maxMs) {
    const job = await getPrintJob(jobId);
    if (job.status === "success" || job.status === "failed") {
      return job;
    }
    await sleep(1500);
  }
  throw new Error("Zeitüberschreitung beim Warten auf den Druckstatus.");
}

function buildPrintTechnicalDetails(result: PrintJobResult): string {
  const details: string[] = [];
  if (result.message) details.push(result.message);
  if (result.cups_job_id) details.push(`CUPS-Job: ${result.cups_job_id}`);
  if (result.cups_job_state) details.push(`Auftragsstatus: ${result.cups_job_state}`);
  if (result.cups_job_status_text) details.push(`CUPS-Status: ${result.cups_job_status_text}`);
  if (result.cups_printer_state) details.push(`Druckerstatus: ${result.cups_printer_state}`);
  if (result.cups_printer_enabled === false) details.push("Drucker ist deaktiviert");
  if (result.issues?.length) details.push(`Hinweise: ${result.issues.join(", ")}`);
  if (result.cups_job_reasons?.length) {
    details.push(`Auftrag: ${result.cups_job_reasons.join(", ")}`);
  }
  if (result.cups_printer_reasons?.length) {
    details.push(`Drucker: ${result.cups_printer_reasons.join(", ")}`);
  }
  return details.join("\n");
}

export function formatPrintJobFeedback(job: PrintJobStatus): PrintJobFeedback {
  if (job.status === "failed") {
    return {
      variant: "error",
      title: "Druck fehlgeschlagen",
      message: job.error || job.result?.message || "Bitte prüfen Sie Drucker und Warteschlange.",
    };
  }

  const result = job.result;
  if (!result) {
    return {
      variant: "warning",
      title: "Druckstatus unklar",
      message: "Bitte prüfen Sie am Drucker, ob das Dokument angekommen ist.",
    };
  }

  if (result.ok === true) {
    const confirmed =
      result.outcome === "completed" || result.outcome === "completed_unverified";
    if (confirmed) {
      return {
        variant: "success",
        title: "Druck erfolgreich",
        message: "",
      };
    }
    return {
      variant: "warning",
      title: "Druck vermutlich erfolgreich",
      message: "Bitte prüfen Sie kurz am Drucker, ob das Dokument angekommen ist.",
    };
  }

  const message = buildPrintTechnicalDetails(result);

  if (result.ok === false || result.outcome === "stuck_in_queue") {
    return {
      variant: "error",
      title: result.outcome === "stuck_in_queue" ? "Druck hängt in der Warteschlange" : "Druck fehlgeschlagen",
      message: message || "Bitte prüfen Sie Drucker und Warteschlange.",
    };
  }

  return {
    variant: "warning",
    title: "Druck nicht eindeutig bestätigt",
    message: message || "Bitte prüfen Sie am Drucker, ob das Dokument angekommen ist.",
  };
}

export function showPrintJobFeedback(job: PrintJobStatus, loadingToastId?: string): PrintJobFeedback {
  if (loadingToastId) toast.dismiss(loadingToastId);
  const feedback = formatPrintJobFeedback(job);
  toast.show({
    variant: feedback.variant,
    title: feedback.title,
    message:
      feedback.variant === "success" || !feedback.message
        ? undefined
        : feedback.message,
  });
  return feedback;
}

export function assertPrintJobSucceeded(job: PrintJobStatus): void {
  const feedback = formatPrintJobFeedback(job);
  if (feedback.variant === "error") {
    throw new PrintJobFeedbackError(job);
  }
}

export async function queuePrintByLocation(
  locationId: number,
  copies: number,
  fileUrl: string,
): Promise<PrintQueueResponse> {
  const body = new URLSearchParams();
  body.set("location_id", String(locationId));
  body.set("copies", String(copies));
  body.set("file_url", fileUrl);
  return api.post<PrintQueueResponse>("/print/by-location", undefined, { rawBody: body });
}

export async function printByLocationAndWait(
  locationId: number,
  copies: number,
  fileUrl: string,
  maxMs = 95000,
): Promise<PrintJobStatus> {
  const queued = await queuePrintByLocation(locationId, copies, fileUrl);
  if (!queued.job_id) {
    throw new Error("Druckauftrag konnte nicht gestartet werden.");
  }
  return waitForPrintJob(queued.job_id, maxMs);
}

export async function runPrintByLocationWithFeedback(
  locationId: number,
  copies: number,
  fileUrl: string,
  options?: { loadingMessage?: string; maxMs?: number },
): Promise<PrintJobStatus> {
  const loadingId = toast.loading(options?.loadingMessage ?? "Druck wird geprüft…");
  try {
    const job = await printByLocationAndWait(locationId, copies, fileUrl, options?.maxMs);
    const feedback = showPrintJobFeedback(job, loadingId);
    if (feedback.variant === "error") {
      throw new PrintJobFeedbackError(job);
    }
    return job;
  } catch (err) {
    if (!(err instanceof PrintJobFeedbackError)) {
      toast.dismiss(loadingId);
    }
    throw err;
  }
}

export type PrintSides = "one-sided" | "two-sided-long-edge" | "duplex-if-multipage";

export type PrintPaperOptions = {
  paperSize?: string | null;
  pagePaperSizes?: string | null;
  /** Deckblatt mehrseitig: doppelseitig. Übrige Dokumente: einseitig. */
  sides?: PrintSides | null;
};

function appendPaperFields(form: FormData, paper?: PrintPaperOptions): void {
  if (paper?.paperSize) form.set("paper_size", paper.paperSize);
  if (paper?.pagePaperSizes) form.set("page_paper_sizes", paper.pagePaperSizes);
  if (paper?.sides) form.set("sides", paper.sides);
}

export async function queuePrintImageByLocation(
  locationId: number,
  copies: number,
  file: File | Blob,
  filename?: string,
  paper?: PrintPaperOptions,
): Promise<PrintQueueResponse> {
  const form = new FormData();
  form.set("location_id", String(locationId));
  form.set("copies", String(copies));
  const name =
    filename ||
    (file instanceof File && file.name ? file.name : "bild.jpg");
  form.set("file", file, name);
  appendPaperFields(form, paper);
  return api.postForm<PrintQueueResponse>("/print/image-by-location", form, {
    timeoutMs: 120_000,
  });
}

export async function queuePrintPdfByLocation(
  locationId: number,
  copies: number,
  file: File | Blob,
  filename?: string,
  paper?: PrintPaperOptions,
): Promise<PrintQueueResponse> {
  const form = new FormData();
  form.set("location_id", String(locationId));
  form.set("copies", String(copies));
  const name =
    filename ||
    (file instanceof File && file.name ? file.name : "dokument.pdf");
  form.set("file", file, name);
  appendPaperFields(form, paper);
  return api.postForm<PrintQueueResponse>("/print/pdf-by-location", form, {
    timeoutMs: 120_000,
  });
}

export type PrintPackFile = {
  blob: Blob;
  filename: string;
  kind: "pdf" | "image";
  pagePaperSizes?: string[] | null;
  sides?: PrintSides | null;
};

export async function queuePrintPackByLocation(
  locationId: number,
  copies: number,
  files: PrintPackFile[],
  title: string,
): Promise<PrintQueueResponse> {
  const form = new FormData();
  form.set("location_id", String(locationId));
  form.set("copies", String(copies));
  form.set("title", title);
  form.set(
    "manifest",
    JSON.stringify(
      files.map((file) => ({
        filename: file.filename,
        kind: file.kind,
        page_paper_sizes: file.pagePaperSizes ?? null,
        sides: file.sides ?? "one-sided",
      })),
    ),
  );
  for (const file of files) {
    form.append("files", file.blob, file.filename);
  }
  return api.postForm<PrintQueueResponse>("/print/pack-by-location", form, {
    timeoutMs: 180_000,
  });
}

export async function printPdfByLocationAndWait(
  locationId: number,
  copies: number,
  file: File | Blob,
  filename?: string,
  maxMs = 95000,
  paper?: PrintPaperOptions,
): Promise<PrintJobStatus> {
  const queued = await queuePrintPdfByLocation(locationId, copies, file, filename, paper);
  if (!queued.job_id) {
    throw new Error("Druckauftrag konnte nicht gestartet werden.");
  }
  return waitForPrintJob(queued.job_id, maxMs);
}

export async function runPrintPdfByLocationWithFeedback(
  locationId: number,
  copies: number,
  file: File | Blob,
  options?: { filename?: string; loadingMessage?: string; maxMs?: number; toastId?: string },
): Promise<PrintJobStatus> {
  const loadingId =
    options?.toastId ?? toast.loading(options?.loadingMessage ?? "PDF wird gedruckt…");
  try {
    const job = await printPdfByLocationAndWait(
      locationId,
      copies,
      file,
      options?.filename,
      options?.maxMs,
    );
    if (!options?.toastId) {
      const feedback = showPrintJobFeedback(job, loadingId);
      if (feedback.variant === "error") {
        throw new PrintJobFeedbackError(job);
      }
    } else {
      assertPrintJobSucceeded(job);
    }
    return job;
  } catch (err) {
    if (!(err instanceof PrintJobFeedbackError) && !options?.toastId) {
      toast.dismiss(loadingId);
    }
    throw err;
  }
}

export async function queuePrintHtmlByLocation(
  locationId: number,
  copies: number,
  html: string,
): Promise<PrintQueueResponse> {
  const body = new URLSearchParams();
  body.set("location_id", String(locationId));
  body.set("copies", String(copies));
  body.set("html", html);
  return api.post<PrintQueueResponse>("/print/html-by-location", undefined, {
    rawBody: body,
    // HTML → PDF (WeasyPrint) kann bei langen Listen dauern.
    timeoutMs: 120_000,
  });
}

export async function printHtmlByLocationAndWait(
  locationId: number,
  copies: number,
  html: string,
  maxMs = 95000,
): Promise<PrintJobStatus> {
  const queued = await queuePrintHtmlByLocation(locationId, copies, html);
  if (!queued.job_id) {
    throw new Error("Druckauftrag konnte nicht gestartet werden.");
  }
  return waitForPrintJob(queued.job_id, maxMs);
}

export async function runPrintHtmlByLocationWithFeedback(
  locationId: number,
  copies: number,
  html: string,
  options?: { loadingMessage?: string; maxMs?: number },
): Promise<PrintJobStatus> {
  const loadingId = toast.loading(options?.loadingMessage ?? "Dokument wird gedruckt…");
  try {
    const job = await printHtmlByLocationAndWait(locationId, copies, html, options?.maxMs);
    const feedback = showPrintJobFeedback(job, loadingId);
    if (feedback.variant === "error") {
      throw new PrintJobFeedbackError(job);
    }
    return job;
  } catch (err) {
    if (!(err instanceof PrintJobFeedbackError)) {
      toast.dismiss(loadingId);
    }
    throw err;
  }
}

export async function printImageByLocationAndWait(
  locationId: number,
  copies: number,
  file: File | Blob,
  filename?: string,
  maxMs = 95000,
  paper?: PrintPaperOptions,
): Promise<PrintJobStatus> {
  const queued = await queuePrintImageByLocation(
    locationId,
    copies,
    file,
    filename,
    paper,
  );
  if (!queued.job_id) {
    throw new Error("Druckauftrag konnte nicht gestartet werden.");
  }
  return waitForPrintJob(queued.job_id, maxMs);
}

export async function runPrintImageByLocationWithFeedback(
  locationId: number,
  copies: number,
  file: File | Blob,
  options?: { filename?: string; loadingMessage?: string; maxMs?: number },
): Promise<PrintJobStatus> {
  const loadingId = toast.loading(options?.loadingMessage ?? "Bild wird gedruckt…");
  try {
    const job = await printImageByLocationAndWait(
      locationId,
      copies,
      file,
      options?.filename,
      options?.maxMs,
    );
    const feedback = showPrintJobFeedback(job, loadingId);
    if (feedback.variant === "error") {
      throw new PrintJobFeedbackError(job);
    }
    return job;
  } catch (err) {
    if (!(err instanceof PrintJobFeedbackError)) {
      toast.dismiss(loadingId);
    }
    throw err;
  }
}

export async function printTestPage(options: {
  printer?: string;
  locationId?: number;
  copies?: number;
}): Promise<PrintQueueResponse> {
  const body = new URLSearchParams();
  if (options.printer) body.set("printer", options.printer);
  if (options.locationId != null) body.set("location_id", String(options.locationId));
  body.set("copies", String(options.copies ?? 1));
  return api.post<PrintQueueResponse>("/print/test-page", undefined, { rawBody: body });
}

export async function printTestPageAndWait(options: {
  printer?: string;
  locationId?: number;
  copies?: number;
}): Promise<PrintJobStatus> {
  const queued = await printTestPage(options);
  if (!queued.job_id) {
    throw new Error("Druckauftrag konnte nicht gestartet werden.");
  }
  return waitForPrintJob(queued.job_id);
}

export async function runPrintTestPageWithFeedback(options: {
  printer?: string;
  locationId?: number;
  copies?: number;
  loadingMessage?: string;
}): Promise<PrintJobStatus> {
  const loadingId = toast.loading(options.loadingMessage ?? "Testseite wird gedruckt…");
  try {
    const job = await printTestPageAndWait(options);
    const feedback = showPrintJobFeedback(job, loadingId);
    if (feedback.variant === "error") {
      throw new PrintJobFeedbackError(job);
    }
    return job;
  } catch (err) {
    if (!(err instanceof PrintJobFeedbackError)) {
      toast.dismiss(loadingId);
    }
    throw err;
  }
}
